import {
    Component, OnInit, inject, signal, computed, CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
    FormBuilder, FormGroup, Validators, ReactiveFormsModule
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StaffService } from '../../../shared/services/staff.service';
import { BusinessService } from '../../../shared/services/business.service';
import { BusinessContextService } from '../../../core/services/business-context.service';
import { ModuleService } from '../../../shared/services/module.service';
import { UserService } from '../../../shared/services/user.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { PermissionModule } from '../../../shared/models';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AppInput } from '../../../shared/components/app-input/app-input';
import { AppBadge } from '../../../shared/components/app-badge/app-badge';
import { LoadingService } from '../../../core/services/loading.service';
import { AppSectionHeader } from "../../../shared/components/app-section-header/app-section-header";

@Component({
    selector: 'app-staff-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, AppButton, AppInput, AppBadge, AppSectionHeader],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styles: [`
    .compact-section {
      background: var(--sidebar-bg);
      border: 1px solid var(--border-color);
      border-radius: 1rem;
      padding: 1.25rem;
      box-shadow: 0 2px 8px -2px rgba(0,0,0,0.04);
    }
    .permission-tile {
      background: var(--sidebar-bg);
      border: 1px solid var(--border-color);
      border-radius: 0.75rem;
      padding: 0.75rem;
      transition: all 0.2s ease;
    }
    .permission-tile:hover {
      border-color: var(--primary);
      opacity: 0.9;
    }
  `],
    templateUrl: './staff-form.html'
})
export class StaffForm implements OnInit {
    private staffService = inject(StaffService);
    private businessService = inject(BusinessService);
    public bizCtx = inject(BusinessContextService);
    private fb = inject(FormBuilder);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private moduleService = inject(ModuleService);
    private userService = inject(UserService);
    private nav = inject(NavigationService);
    private loadingService = inject(LoadingService);

    paramsSignal = toSignal(this.route.params);
    querySignal = toSignal(this.route.queryParams);

    // Detección robusta de IDs basada en la estructura de la ruta
    get memberId(): string | null {
        const params = this.paramsSignal() as any;
        if (!params) return null;

        // 1. Prioridad: staffId explícito en la ruta (rutas anidadas en business)
        if (params['staffId']) return params['staffId'];

        // 2. Si es la ruta /staff/edit/:id, el parámetro 'id' es el staff
        const url = this.router.url;
        if (url.startsWith('/app/staff/edit/') || url.includes('/staff/edit/')) {
            return params['id'];
        }

        return null;
    }

    get bizId(): string | null {
        const params = this.paramsSignal() as any;
        const qParams = this.querySignal() as any;
        if (!params) return null;

        // Si la URL contiene /business/edit/ el parámetro 'id' siempre es el negocio
        if (this.router.url.includes('/business/edit/')) {
            return params['id'];
        }

        // Fallback a query params o al contexto activo
        return qParams?.['business'] || this.bizCtx.business() || null;
    }

    get isEditRoute(): boolean {
        const url = this.router.url;
        // Si la ruta contiene explicitly 'new', NO es edición
        if (url.endsWith('/new') || url.includes('/new?')) return false;

        // Es edición si tiene staffId o si estamos en una ruta de edición de staff clara
        const params = this.paramsSignal() as any;
        if (params?.['staffId']) return true;

        return url.startsWith('/app/staff/edit/') || url.includes('/staff/edit/');
    }

    saving = signal(false);
    searchingEmail = signal(false);
    userExists = signal(false);
    userAlreadyInBusiness = signal(false);
    error = signal('');
    isEdit = signal(false);
    business = signal<string | null>(null);
    targetBusiness = signal<any | null>(null);

    readonly businessName = computed(() => this.targetBusiness()?.commercialName || this.targetBusiness()?.name || this.bizCtx.businessName());

    allDbModules = signal<any[]>([]);

    readonly availableModules = computed<PermissionModule[]>(() => {
        const dbModules: any[] = this.allDbModules();
        const allowed: string[] = this.targetBusiness()?.allowedActions || this.bizCtx.allowedActions() || [];

        const all: PermissionModule[] = dbModules
            .filter(m => m.group !== 'system')
            .map(m => ({
                module: m.key,
                label: m.label,
                icon: m.icon,
                actions: (m.permissions || []).map((p: string) => {
                    const actionKey = p.split(':')[1] || p;
                    const labels: Record<string, string> = {
                        list: 'Listar', create: 'Registrar', edit: 'Editar',
                        delete: 'Eliminar', export: 'Exportar', print: 'Imprimir',
                        sync: 'Sincronizar', manage: 'Gestionar',
                        odontogram: 'Odontograma', sri: 'Facturación SRI'
                    };
                    return {
                        key: actionKey,
                        full: p,
                        label: labels[actionKey] || actionKey.charAt(0).toUpperCase() + actionKey.slice(1),
                        default: actionKey === 'list'
                    };
                })
            }));

        if (allowed.includes('*')) return all;

        return all
            .filter(mod => mod.actions.some((a: any) => allowed.includes('*') || allowed.includes(a.full)))
            .map(mod => ({
                ...mod,
                actions: mod.actions.filter((a: any) => allowed.includes('*') || allowed.includes(a.full))
            }));
    });

    selectedActions = signal<string[]>([]);
    readonly selectedActionCount = computed(() => this.selectedActions().length);

    form: FormGroup;

    constructor() {
        this.form = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            fullName: ['', Validators.required],
            password: ['', [Validators.minLength(6)]],
            isActive: [true],
            isOdontologist: [false],
        });
    }

    goBack() {
        this.nav.goBack();
    }

    ngOnInit() {
        this.loadModules();
        this.isEdit.set(this.isEditRoute);

        const mId = this.memberId;
        const bId = this.bizId;

        if (bId) {
            this.business.set(bId);
            this.loadBusiness(bId);
        }

        if (this.isEdit()) {
            this.form.get('password')?.clearValidators();
            this.form.get('password')?.updateValueAndValidity();
            if (mId) {
                this.loadStaff(mId);
            }
        } else {
            this.form.get('password')?.addValidators([Validators.required]);
            this.form.get('password')?.updateValueAndValidity();
            this.listenEmailChanges();
        }
    }

    private listenEmailChanges() {
        this.form.get('email')?.valueChanges.subscribe(email => {
            if (this.isEdit() || !email || !this.form.get('email')?.valid) {
                if (!this.isEdit() && !this.userExists()) {
                    this.resetUserExistence();
                }
                return;
            }

            this.searchingEmail.set(true);
            this.userService.checkEmail(email).subscribe({
                next: (res) => {
                    this.searchingEmail.set(false);
                    if (res.exists && res.user) {
                        this.userExists.set(true);
                        this.userAlreadyInBusiness.set(!!res.alreadyInBusiness);
                        this.form.get('fullName')?.setValue(res.user.fullName);
                        this.form.get('fullName')?.disable();
                        this.form.get('password')?.clearValidators();
                        this.form.get('password')?.updateValueAndValidity();
                    } else {
                        this.resetUserExistence();
                    }
                },
                error: () => {
                    this.searchingEmail.set(false);
                    this.resetUserExistence();
                }
            });
        });
    }

    private resetUserExistence() {
        this.userExists.set(false);
        this.userAlreadyInBusiness.set(false);
        this.form.get('fullName')?.enable();
        if (!this.isEdit()) {
            this.form.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
            this.form.get('password')?.updateValueAndValidity();
        }
    }

    private loadModules() {
        this.moduleService.loadAll({ limit: 100 }).subscribe({
            next: (res) => this.allDbModules.set(res.data || [])
        });
    }

    private loadBusiness(id: string) {
        this.businessService.getById(id).subscribe({
            next: (res: any) => this.targetBusiness.set(res.data || res),
            error: () => this.error.set('No se pudo cargar la información del negocio')
        });
    }

    private loadStaff(id: string) {
        this.loadingService.showLoading('Cargando Colaborador', 'Obteniendo perfil y permisos...');
        this.staffService.getById(id).subscribe({
            next: (resp: any) => {
                const Staff = resp?.data ?? resp;
                const user = Staff.user;
                this.form.patchValue({
                    fullName: user.fullName,
                    email: user.email,
                    isActive: Staff.isActive,
                    isOdontologist: Staff.isOdontologist === true
                });
                if (Staff.permissions) {
                    // Normalizar permisos legacy a nuevos para que la Interfaz seleccione las casilleros correctamente
                    const legacyMap: Record<string, string> = {
                        'users:read': 'users:list',
                        'users:write': 'users:create',
                        'patients:read': 'patients:list',
                        'patients:write': 'patients:create',
                        'appointments:read': 'appointments:list',
                        'appointments:write': 'appointments:create',
                        'staff:read': 'staff:list',
                        'staff:write': 'staff:create',
                        'business:read': 'business:list',
                        'business:write': 'business:edit',
                        'medical-records:consents': 'consents:list'
                    };
                    const normalizedPerms: string[] = Staff.permissions.map((p: string) => legacyMap[p] || p);
                    this.selectedActions.set(Array.from(new Set<string>(normalizedPerms)));
                }

                // Si no teníamos business de la ruta, lo sacamos del staff cargado
                if (!this.business() && Staff.business) {
                    const bId = typeof Staff.business === 'object' ? Staff.business._id : Staff.business;
                    this.business.set(bId);
                    this.loadBusiness(bId);
                }

                this.loadingService.hideLoading();
            },
            error: () => {
                this.error.set('Error al recuperar información del colaborador');
                this.loadingService.hideLoading();
            },
        });
    }

    isModuleEnabled(mod: PermissionModule): boolean {
        if (this.selectedActions().includes('*')) return true;
        return mod.actions.length > 0 && mod.actions.every(a => this.selectedActions().includes(a.full));
    }
    isActionSelected(fullKey: string): boolean {
        return this.selectedActions().includes(fullKey);
    }
    toggleModule(mod: PermissionModule) {
        const current = this.selectedActions();
        const modActions = mod.actions.map(a => a.full);
        const isAllSelected = modActions.every(a => current.includes(a));

        if (isAllSelected) {
            this.selectedActions.set(current.filter(a => !modActions.includes(a)));
        } else {
            this.selectedActions.set([...new Set([...current, ...modActions])]);
        }
    }
    toggleAction(module: string, actionKey: string) {
        const mod = this.availableModules().find(m => m.module === module);
        const action = mod?.actions.find(a => a.key === actionKey);
        if (!action) return;

        const key = action.full;
        const allowed = this.targetBusiness()?.allowedActions || this.bizCtx.allowedActions();
        if (!allowed.includes('*') && !allowed.includes(key)) return;

        this.selectedActions.update(actions =>
            actions.includes(key) ? actions.filter(a => a !== key) : [...actions, key]
        );
    }
    selectAllActions() {
        const all = this.availableModules().flatMap(mod =>
            mod.actions.map(a => a.full)
        );
        this.selectedActions.set([...new Set(all)]);
    }
    clearActions() {
        this.selectedActions.set([]);
    }

    save() {
        if (this.form.invalid || (this.selectedActionCount() === 0)) return;
        this.saving.set(true);
        this.error.set('');

        const val = this.form.getRawValue();
        const memberId = this.memberId;

        if (this.isEdit()) {
            this.staffService.update(memberId!, {
                permissions: this.selectedActions(),
                isActive: val.isActive,
                isOdontologist: val.isOdontologist
            }).subscribe({
                next: () => {
                    this.saving.set(false);
                    this.goBack();
                },
                error: (err: any) => {
                    this.saving.set(false);
                    this.error.set(err?.error?.message || 'Error al actualizar colaborador');
                }
            });
        } else {
            this.staffService.invite({
                email: val.email,
                fullName: val.fullName,
                password: val.password,
                permissions: this.selectedActions(),
                business: this.business() || undefined,
                isOdontologist: val.isOdontologist
            }).subscribe({
                next: () => {
                    this.saving.set(false);
                    this.goBack();
                },
                error: (err: any) => {
                    this.saving.set(false);
                    this.error.set(err?.error?.message || 'Error al invitar colaborador');
                }
            });
        }
    }
}
